// src/pages/company/CompanySubscribePage.jsx
import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import CompanyLayout from "../../components/company/CompanyLayout";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { metaTrack, gaTrack, markStripeIntent } from "../../lib/analytics";
import { isIOS } from "../../lib/platform";

const FREE_CONTRACT_LIMIT = 5;

// Pulls the numeric USD amount out of a tier's display string like "$5,000/year".
function parsePrice(priceStr) {
  if (!priceStr) return null;
  const match = priceStr.match(/[\d,]+(\.\d+)?/);
  if (!match) return null;
  return parseFloat(match[0].replace(/,/g, ""));
}

const pricingTiers = [
  {
    key: "small",
    label: "Small (<25 Employees)",
    annual: "$1,000/year",
    monthly: "$100/month",
    priceIdAnnual: "price_1RHUf3PPTvUjH7LxO8I70MCs",
    priceIdMonthly: "price_1RHUf3PPTvUjH7Lxqs6O8Qwy",
  },
  {
    key: "medium",
    label: "Medium (25–100 Employees)",
    annual: "$5,000/year",
    monthly: "$450/month",
    priceIdAnnual: "price_1RHUpaPPTvUjH7LxcaT6mqZv",
    priceIdMonthly: "price_1RHUpaPPTvUjH7Lx8CXC7F5O",
  },
  {
    key: "large",
    label: "Large (100–500 Employees)",
    annual: "$15,000/year",
    monthly: null,
    priceIdAnnual: "price_1RHUrCPPTvUjH7LxmL6qtxFw",
  },
  {
    key: "enterprise",
    label: "Enterprise (500+ Employees)",
    annual: "$25,000/year",
    monthly: null,
    priceIdAnnual: "price_1RHUsAPPTvUjH7LxZoTJbSNK",
  },
];

// App Store guideline 3.1.1: don't expose subscription tier pricing
// or Stripe checkout flows inside the iOS app. Org admins manage
// their plan on the web.
function IOSBillingNotice() {
  return (
    <CompanyLayout>
      <div className="p-6 max-w-2xl mx-auto text-center text-ink">
        <h1 className="text-2xl font-display italic text-ink mb-3">Manage your subscription on the web</h1>
        <p className="text-tertiary">
          Subscription management for Sift Careers is handled at{" "}
          <a href="https://siftcareers.com" className="text-brand hover:text-brand-hover no-underline">
            siftcareers.com
          </a>
          . Sign in on the web to view, change, or cancel your organization's plan.
        </p>
      </div>
    </CompanyLayout>
  );
}

export default function OrganizationSubscribePage() {
  if (isIOS()) return <IOSBillingNotice />;
  return <OrganizationSubscribePageInner />;
}

function OrganizationSubscribePageInner() {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [canceling, setCanceling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  // Subscription status - similar to contractor stripe page
  const [subscriptionStatus, setSubscriptionStatus] = useState("not_subscribed"); 
  // possible values: not_subscribed, free_tier, active, cancel_at_period_end, expired, error

  // Stripe Connect (payouts) status
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [stripeChargesEnabled, setStripeChargesEnabled] = useState(false);
  const [stripePayoutsEnabled, setStripePayoutsEnabled] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);

  // Free tier contract tracking
  const [contractsUsed, setContractsUsed] = useState(0);
  const [contractsRemaining, setContractsRemaining] = useState(FREE_CONTRACT_LIMIT);

  useEffect(() => {
    (async () => {
      try {
        const me = await fetchWithAuth("/api/accounts/me/");
        setUser(me);
        const activeOrg = me?.organization || null;
        setOrg(activeOrg);

        if (!activeOrg) {
          setCheckingStatus(false);
          setLoading(false);
          return;
        }

        // Determine subscription status
        const { is_subscribed, subscription_status, subscription_end_date, subscription_plan } = activeOrg;
        
        if (!is_subscribed) {
          setSubscriptionStatus("free_tier");
        } else if (subscription_status === "cancel_at_period_end") {
          if (subscription_end_date && new Date(subscription_end_date) < new Date()) {
            setSubscriptionStatus("expired");
            setSubscriptionExpired(true);
          } else {
            setSubscriptionStatus("cancel_at_period_end");
          }
        } else if (subscription_status === "active") {
          setSubscriptionStatus("active");
        } else {
          setSubscriptionStatus("not_subscribed");
        }

        // Check Stripe Connect status
        const acct = activeOrg?.direct_deposit?.stripe_account_id || activeOrg?.stripe_account_id;
        if (acct) {
          setStripeConnected(true);
          setStripeAccountId(acct);
        }
        // Always check detailed status endpoint for capabilities
        try {
          const status = await fetchWithAuth("/api/contracts/stripe/org/status/");
          if (status?.connected) {
            setStripeConnected(true);
            setStripeAccountId(status.account_id || acct || null);
            setStripeChargesEnabled(status.charges_enabled || false);
            setStripePayoutsEnabled(status.payouts_enabled || false);
          }
        } catch {}

        // Fetch contract count for free tier tracking
        try {
          const contractsRes = await fetchWithAuth("/api/contracts/company/contracts/");
          const contractsData = Array.isArray(contractsRes) ? contractsRes : contractsRes?.results || [];
          const count = contractsData.length;
          setContractsUsed(count);
          setContractsRemaining(Math.max(0, FREE_CONTRACT_LIMIT - count));
        } catch {
          // If we can't fetch, check if org has contract_count field
          const count = activeOrg?.contract_count || activeOrg?.contracts_count || 0;
          setContractsUsed(count);
          setContractsRemaining(Math.max(0, FREE_CONTRACT_LIMIT - count));
        }
      } catch (err) {
        console.error("Failed to load user data:", err);
        setSubscriptionStatus("error");
      } finally {
        setLoading(false);
        setCheckingStatus(false);
      }
    })();
  }, []);

  // ---------- Subscription flows ----------
  const handleSubscribe = async (price_id, value) => {
    if (!org) {
      alert("You must belong to an organization to subscribe.");
      return;
    }
    // Fire InitiateCheckout before redirect and stash the intended value so
    // the return URL (?subscribed=1) can fire Subscribe/Purchase with value.
    const numericValue = typeof value === "number" ? value : 0;
    metaTrack("InitiateCheckout", { currency: "USD", value: numericValue });
    gaTrack("begin_checkout", { currency: "USD", value: numericValue, items: [{ item_id: price_id, item_name: "Subscription", price: numericValue, quantity: 1 }] });
    markStripeIntent({ type: "subscribe", value: numericValue, currency: "USD" });
    try {
      const res = await fetchWithAuth("/api/contracts/stripe/subscribe/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_id }),
      });
      if (!res?.url) throw new Error("Stripe URL missing in response");
      window.location.href = res.url;
    } catch (err) {
      console.error("Subscription error:", err);
      alert("Subscription failed. Please try again.");
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const res = await fetchWithAuth("/api/contracts/stripe/billing-portal/", { method: "POST" });
      if (res?.url) window.location.href = res.url;
      else alert("Failed to open billing portal.");
    } catch (err) {
      console.error("Billing portal error:", err);
      alert("Failed to open billing portal.");
    }
  };

  const handleCancelSubscription = async (cancel_reason) => {
    if (!org) {
      alert("You must belong to an organization to cancel.");
      return;
    }
    setCanceling(true);
    try {
      const res = await fetchWithAuth("/api/contracts/stripe/cancel-subscription/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cancel_reason }),
      });
      if (res?.success) {
        window.location.reload();
      } else {
        alert("Failed to cancel subscription.");
      }
    } catch (err) {
      console.error("Cancel subscription error:", err);
      alert("Failed to cancel subscription.");
    } finally {
      setCanceling(false);
      setShowCancelModal(false);
    }
  };

  // ---------- Stripe Connect (payouts) ----------
  const connectOrgStripe = async () => {
    setConnectingStripe(true);
    try {
      const res = await fetchWithAuth("/api/contracts/create-org-stripe-account/", { method: "POST" });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      alert("Could not start Stripe onboarding.");
    } catch (e) {
      console.error(e);
      alert("Could not start Stripe onboarding.");
    } finally {
      setConnectingStripe(false);
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <h1 className="text-3xl font-display italic text-ink">Organization subscription</h1>
    </div>
  );

  const Content = () => {
    if (loading || checkingStatus) return <PageLoadingInLayout />;
    if (!user) return <PageBlurb message="Failed to load user information." />;
    if (!org) return <PageBlurb message="You must join or create an organization first." />;

    const { subscription_plan, subscription_end_date, role } = org;

    return (
      <div className="space-y-6">
        {/* Subscription Status Card - Similar to Contractor Stripe Page */}
        <SectionCard title="Subscription Status">
          <div className="mb-6">
            <div className="text-sm text-tertiary mb-2">Current Status</div>
            <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
              subscriptionStatus === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              subscriptionStatus === "cancel_at_period_end" ? "bg-amber-50 text-amber-700 border border-amber-200" :
              subscriptionStatus === "expired" ? "bg-red-50 text-red-700 border border-red-200" :
              subscriptionStatus === "free_tier" ? "bg-brand-tint text-brand border border-line" :
              "bg-sunken text-tertiary border border-line"
            }`}>
              {subscriptionStatus === "active" ? "✓ Active Subscription" :
               subscriptionStatus === "cancel_at_period_end" ? "⚠ Canceling at Period End" :
               subscriptionStatus === "expired" ? "❌ Expired" :
               subscriptionStatus === "free_tier" ? `🆓 Free Tier (${contractsUsed}/${FREE_CONTRACT_LIMIT} Contracts)` :
               subscriptionStatus === "error" ? "Error" :
               "Not Subscribed"}
            </div>
          </div>

          {subscriptionStatus === "active" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="font-semibold text-emerald-700 mb-2">✓ Subscription Active</div>
              <div className="text-sm text-secondary space-y-1">
                <div><strong>Plan:</strong> {subscription_plan ? subscription_plan.toUpperCase() : "Unknown"}</div>
                {subscription_end_date && (
                  <div><strong>Next Renewal:</strong> {new Date(subscription_end_date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}</div>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleOpenBillingPortal}
                  className="bg-brand hover:bg-brand-hover px-4 py-2 rounded-pill text-white text-sm"
                >
                  Manage Billing
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={canceling}
                  className="border border-line-strong bg-surface text-ink hover:border-red-400 hover:text-red-700 px-4 py-2 rounded-pill text-sm disabled:opacity-50"
                >
                  {canceling ? "Cancelling..." : "Cancel Subscription"}
                </button>
              </div>
            </div>
          )}

          {subscriptionStatus === "cancel_at_period_end" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="font-semibold text-amber-700 mb-2">⚠ Canceling at Period End</div>
              <div className="text-sm text-secondary">
                Your subscription will end on {new Date(subscription_end_date).toLocaleDateString()}
              </div>
              <button
                onClick={handleOpenBillingPortal}
                className="mt-3 bg-brand hover:bg-brand-hover px-4 py-2 rounded-pill text-white text-sm"
              >
                Reactivate Subscription
              </button>
            </div>
          )}

          {subscriptionStatus === "free_tier" && (
            <div className="bg-brand-tint border border-line rounded-lg p-4 mb-4">
              <div className="font-semibold text-brand mb-2">🆓 Free Tier Active</div>
              <div className="text-sm text-secondary mb-3">
                You can create up to {FREE_CONTRACT_LIMIT} contracts for free. Upgrade anytime to unlock unlimited contracts and premium features.
              </div>

              {/* Contract usage display */}
              <div className="bg-sunken rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-tertiary">Contracts Used</span>
                  <span className={`text-sm font-semibold ${contractsRemaining === 0 ? 'text-red-700' : contractsRemaining <= 2 ? 'text-amber-600' : 'text-emerald-700'}`}>
                    {contractsUsed} / {FREE_CONTRACT_LIMIT}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="w-full bg-line rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      contractsRemaining === 0 ? 'bg-red-500' :
                      contractsRemaining <= 2 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, (contractsUsed / FREE_CONTRACT_LIMIT) * 100)}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-tertiary">
                  {contractsRemaining === 0 ? (
                    <span className="text-red-700">You've used all free contracts. Upgrade to continue.</span>
                  ) : contractsRemaining === 1 ? (
                    <span className="text-amber-600">Only 1 free contract remaining!</span>
                  ) : (
                    <span>{contractsRemaining} free contract{contractsRemaining !== 1 ? 's' : ''} remaining</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {(subscriptionStatus === "free_tier" || subscriptionStatus === "expired") && (
            <div className="mt-4">
              <h3 className="text-lg font-display italic text-ink mb-3">Choose a plan</h3>
              <PricingTiersView oldPlanKey={subscription_plan} handleSubscribe={handleSubscribe} />
            </div>
          )}
        </SectionCard>

        {/* Stripe Connect Card */}
        <SectionCard title="Payment Processing (Stripe Connect)">
          <div className="text-sm text-secondary mb-3">
            Connect your organization's Stripe account to make and receive payments on contracts.
          </div>

          {stripeConnected ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                <div className="font-semibold text-emerald-700">Stripe Connected</div>
              </div>

              {stripeAccountId && (
                <div className="text-sm text-tertiary mb-3">Account ID: {maskAcct(stripeAccountId)}</div>
              )}

              {/* Capabilities status */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className={`text-sm px-3 py-2 rounded ${stripeChargesEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-sunken text-faint'}`}>
                  {stripeChargesEnabled ? '✓' : '○'} Charges {stripeChargesEnabled ? 'Enabled' : 'Pending'}
                </div>
                <div className={`text-sm px-3 py-2 rounded ${stripePayoutsEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-sunken text-faint'}`}>
                  {stripePayoutsEnabled ? '✓' : '○'} Payouts {stripePayoutsEnabled ? 'Enabled' : 'Pending'}
                </div>
              </div>

              {(!stripeChargesEnabled || !stripePayoutsEnabled) && (
                <div className="text-xs text-amber-600 mb-3">
                  ⚠ Complete Stripe onboarding to enable all payment features.
                </div>
              )}

              <div className="text-sm text-secondary">
                {stripeChargesEnabled && stripePayoutsEnabled
                  ? "You're fully set up to make and receive payments on contracts."
                  : "Your account is connected but may need additional verification."}
              </div>

              <button
                onClick={connectOrgStripe}
                disabled={connectingStripe}
                className="mt-3 border border-line-strong bg-surface text-ink hover:border-brand hover:text-brand px-4 py-2 rounded-pill text-sm"
              >
                {connectingStripe ? "Loading..." : "Update Stripe Settings"}
              </button>
            </div>
          ) : (
            <div className="bg-sunken border border-line rounded-lg p-4">
              <div className="font-semibold text-tertiary mb-2">Not Connected</div>
              <div className="text-sm text-secondary mb-3">
                You need to connect Stripe to process payments.
              </div>
              <button
                onClick={connectOrgStripe}
                disabled={connectingStripe}
                className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-pill disabled:opacity-50 text-sm"
              >
                {connectingStripe ? "Starting..." : "Connect Stripe"}
              </button>
            </div>
          )}

          {role && (
            <div className="text-xs text-faint mt-3">
              Note: Org owners/managers may be required to complete onboarding.
            </div>
          )}
        </SectionCard>
      </div>
    );
  };

  return (
    <CompanyLayout org={org} header={header}>
      <Content />
      <CancelReasonModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSubmit={handleCancelSubscription}
        canceling={canceling}
      />
    </CompanyLayout>
  );
}

/* ---------- Helper Components ---------- */

function maskAcct(id) {
  if (!id) return "";
  const last = id.slice(-4);
  return `acct_…${last}`;
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      {title ? <h2 className="text-xl font-display italic text-ink mb-4">{title}</h2> : null}
      {children}
    </div>
  );
}

function PageLoadingInLayout() {
  return (
    <div className="bg-surface border border-line rounded-2xl p-6 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-3"></div>
        <p className="text-tertiary">Loading...</p>
      </div>
    </div>
  );
}

function PageBlurb({ message }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <p className="text-sm text-secondary">{message}</p>
    </div>
  );
}

function PricingTiersView({ oldPlanKey, handleSubscribe }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {pricingTiers.map((tier) => (
        <div
          key={tier.key}
          className={`relative rounded-2xl p-6 shadow-sm flex flex-col justify-between transition-all transform
            ${tier.key === oldPlanKey ? "bg-surface border-2 border-brand" : "bg-surface border border-line"}
            hover:md:scale-105 hover:shadow-lg
          `}
        >
          {tier.key === oldPlanKey && (
            <div className="absolute top-2 right-2 bg-brand-tint text-brand border border-brand px-2 py-1 text-xs font-bold rounded-pill">
              Recommended
            </div>
          )}
          <div>
            <h3 className="text-xl font-display italic text-ink mb-2">{tier.label}</h3>
            <p className="text-ink mb-1" style={{ fontFamily: "'Bodoni Moda',serif" }}>{tier.annual}</p>
            {tier.monthly && <p className="text-secondary" style={{ fontFamily: "'Bodoni Moda',serif" }}>{tier.monthly}</p>}
          </div>
          <div className="flex flex-col space-y-2 mt-6">
            {tier.priceIdAnnual && (
              <button
                onClick={() => handleSubscribe(tier.priceIdAnnual, parsePrice(tier.annual))}
                className="bg-brand hover:bg-brand-hover px-4 py-2 rounded-pill text-white transition-all"
              >
                Subscribe (Annual)
              </button>
            )}
            {tier.priceIdMonthly && (
              <button
                onClick={() => handleSubscribe(tier.priceIdMonthly, parsePrice(tier.monthly))}
                className="border border-line-strong bg-surface text-ink hover:border-brand hover:text-brand px-4 py-2 rounded-pill transition-all"
              >
                Subscribe (Monthly)
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CancelReasonModal({ isOpen, onClose, onSubmit, canceling }) {
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    if (reason.trim() === "") {
      alert("Please select a reason.");
      return;
    }
    onSubmit(reason);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="bg-black/50 absolute inset-0" />
      <div className="bg-surface border border-line rounded-2xl shadow-xl p-6 z-10 w-full max-w-md mx-auto relative">
        <Dialog.Title className="text-lg font-display italic mb-4 text-ink">Why are you cancelling?</Dialog.Title>

        <select aria-label="Reason for cancelling"
          className="w-full p-2 border border-line-control rounded-lg bg-surface text-ink mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">Select a reason</option>
          <option value="Too expensive">Too expensive</option>
          <option value="No longer needed">No longer needed</option>
          <option value="Switching to competitor">Switching to competitor</option>
          <option value="Not satisfied with service">Not satisfied with service</option>
          <option value="Other">Other</option>
        </select>

        <div className="flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 border border-line-strong bg-surface text-ink rounded-pill hover:border-brand hover:text-brand">
            Close
          </button>
          <button
            onClick={handleSubmit}
            disabled={canceling}
            className="px-4 py-2 bg-red-600 text-white rounded-pill hover:bg-red-700 disabled:opacity-50"
          >
            {canceling ? "Cancelling..." : "Confirm Cancel"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
